import React, { useEffect, useRef, useState } from 'react';
import * as d3 from 'd3';

interface GraphNode {
  id: string;
  name: string;
  version: string;
  type: string;
  tags: string[];
}

interface GraphLink {
  source: string;
  target: string;
  type: string;
  label: string;
}

interface GraphData {
  nodes: GraphNode[];
  links: GraphLink[];
}

interface Props {
  data: GraphData;
  onNodeClick?: (node: GraphNode) => void;
}

export function GraphVisualization({ data, onNodeClick }: Props) {
  const svgRef = useRef<SVGSVGElement>(null);
  const [selectedNode, setSelectedNode] = useState<string | null>(null);

  useEffect(() => {
    if (!svgRef.current || !data.nodes.length) return;

    // Clear previous graph
    d3.select(svgRef.current).selectAll('*').remove();

    // Set up dimensions
    const width = svgRef.current.clientWidth;
    const height = svgRef.current.clientHeight;

    // Create SVG
    const svg = d3
      .select(svgRef.current)
      .attr('width', width)
      .attr('height', height);

    // Add zoom behavior
    const g = svg.append('g');
    const zoom = d3.zoom<SVGSVGElement, unknown>()
      .scaleExtent([0.1, 4])
      .on('zoom', (event) => {
        g.attr('transform', event.transform);
      });
    svg.call(zoom);

    // Prepare data for D3
    const nodes = data.nodes.map((d) => ({ ...d }));
    const links = data.links.map((d) => ({
      source: d.source,
      target: d.target,
      type: d.type,
      label: d.label,
    }));

    // Create force simulation
    const simulation = d3
      .forceSimulation(nodes as any)
      .force(
        'link',
        d3
          .forceLink(links)
          .id((d: any) => d.id)
          .distance(150)
      )
      .force('charge', d3.forceManyBody().strength(-400))
      .force('center', d3.forceCenter(width / 2, height / 2))
      .force('collision', d3.forceCollide().radius(50));

    // Create arrow markers for directed edges
    const defs = svg.append('defs');

    // Arrow for dependencies (blue)
    defs
      .append('marker')
      .attr('id', 'arrow-depends')
      .attr('viewBox', '0 -5 10 10')
      .attr('refX', 25)
      .attr('refY', 0)
      .attr('markerWidth', 6)
      .attr('markerHeight', 6)
      .attr('orient', 'auto')
      .append('path')
      .attr('d', 'M0,-5L10,0L0,5')
      .attr('fill', '#3b82f6');

    // Arrow for conflicts (red)
    defs
      .append('marker')
      .attr('id', 'arrow-conflicts')
      .attr('viewBox', '0 -5 10 10')
      .attr('refX', 25)
      .attr('refY', 0)
      .attr('markerWidth', 6)
      .attr('markerHeight', 6)
      .attr('orient', 'auto')
      .append('path')
      .attr('d', 'M0,-5L10,0L0,5')
      .attr('fill', '#ef4444');

    // Create links
    const link = g
      .append('g')
      .selectAll('line')
      .data(links)
      .join('line')
      .attr('stroke', (d) =>
        d.type === 'DEPENDS_ON' ? '#3b82f6' : '#ef4444'
      )
      .attr('stroke-width', 2)
      .attr('marker-end', (d) =>
        d.type === 'DEPENDS_ON' ? 'url(#arrow-depends)' : 'url(#arrow-conflicts)'
      )
      .attr('opacity', 0.6);

    // Create link labels
    const linkLabel = g
      .append('g')
      .selectAll('text')
      .data(links)
      .join('text')
      .attr('font-size', 10)
      .attr('fill', '#6b7280')
      .attr('text-anchor', 'middle')
      .text((d) => d.label);

    // Create node groups
    const node = g
      .append('g')
      .selectAll('g')
      .data(nodes)
      .join('g')
      .attr('cursor', 'pointer')
      .call(
        d3.drag<any, any>()
          .on('start', dragStarted)
          .on('drag', dragged)
          .on('end', dragEnded)
      )
      .on('click', (event, d) => {
        setSelectedNode(d.id);
        if (onNodeClick) {
          onNodeClick(d as GraphNode);
        }
      });

    // Add circles for nodes
    node
      .append('circle')
      .attr('r', 20)
      .attr('fill', (d) => getNodeColor(d.type))
      .attr('stroke', (d) => (selectedNode === d.id ? '#000' : '#fff'))
      .attr('stroke-width', (d) => (selectedNode === d.id ? 3 : 2));

    // Add labels
    node
      .append('text')
      .attr('dy', -25)
      .attr('text-anchor', 'middle')
      .attr('font-size', 12)
      .attr('font-weight', 'bold')
      .attr('fill', '#1f2937')
      .text((d) => d.name);

    // Add version labels
    node
      .append('text')
      .attr('dy', 35)
      .attr('text-anchor', 'middle')
      .attr('font-size', 10)
      .attr('fill', '#6b7280')
      .text((d) => `v${d.version}`);

    // Update positions on simulation tick
    simulation.on('tick', () => {
      link
        .attr('x1', (d: any) => d.source.x)
        .attr('y1', (d: any) => d.source.y)
        .attr('x2', (d: any) => d.target.x)
        .attr('y2', (d: any) => d.target.y);

      linkLabel
        .attr('x', (d: any) => (d.source.x + d.target.x) / 2)
        .attr('y', (d: any) => (d.source.y + d.target.y) / 2);

      node.attr('transform', (d: any) => `translate(${d.x},${d.y})`);
    });

    // Drag functions
    function dragStarted(event: any) {
      if (!event.active) simulation.alphaTarget(0.3).restart();
      event.subject.fx = event.subject.x;
      event.subject.fy = event.subject.y;
    }

    function dragged(event: any) {
      event.subject.fx = event.x;
      event.subject.fy = event.y;
    }

    function dragEnded(event: any) {
      if (!event.active) simulation.alphaTarget(0);
      event.subject.fx = null;
      event.subject.fy = null;
    }

    // Cleanup
    return () => {
      simulation.stop();
    };
  }, [data, selectedNode, onNodeClick]);

  return (
    <div className="relative w-full h-full">
      <svg ref={svgRef} className="w-full h-full border rounded-lg bg-white" />
      <div className="absolute top-4 right-4 bg-white border rounded-lg p-3 shadow-md text-sm">
        <div className="font-semibold mb-2">Legend</div>
        <div className="flex items-center gap-2 mb-1">
          <div className="w-4 h-4 rounded-full bg-blue-500"></div>
          <span>Document</span>
        </div>
        <div className="flex items-center gap-2 mb-1">
          <div className="w-4 h-4 rounded-full bg-green-500"></div>
          <span>Prompt</span>
        </div>
        <div className="flex items-center gap-2 mb-1">
          <div className="w-4 h-4 rounded-full bg-purple-500"></div>
          <span>Spec</span>
        </div>
        <div className="flex items-center gap-2 mb-3">
          <div className="w-4 h-4 rounded-full bg-orange-500"></div>
          <span>Example</span>
        </div>
        <div className="flex items-center gap-2 mb-1">
          <div className="w-8 h-0.5 bg-blue-500"></div>
          <span>Depends On</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-8 h-0.5 bg-red-500"></div>
          <span>Conflicts</span>
        </div>
      </div>
      <div className="absolute bottom-4 left-4 bg-white border rounded-lg p-2 shadow-md text-xs text-gray-600">
        <div>🖱️ Drag nodes to reposition</div>
        <div>🔍 Scroll to zoom</div>
        <div>👆 Click node for details</div>
      </div>
    </div>
  );
}

function getNodeColor(type: string): string {
  switch (type.toLowerCase()) {
    case 'document':
      return '#3b82f6'; // blue
    case 'prompt':
      return '#10b981'; // green
    case 'spec':
      return '#8b5cf6'; // purple
    case 'example':
      return '#f97316'; // orange
    default:
      return '#6b7280'; // gray
  }
}
